/**
 * Market Performance — SP1500, NYSE and NASDAQ stacked one on another
 * (decision 4), four rows each: Sector A/D, Industry A/D, % Advance, % Above
 * Mid BB. Each row is today's bar over the 2D one, today's value, the change
 * in points, and the 2D value.
 */
import type { CSSProperties } from "react";
import { fetchBriefMarket, type DayPair, type MarketList, type MarketPayload } from "../../api/daily-brief";
import { DualGauge, MUTED, PREV, PointDelta } from "./cells";
import { useBriefData } from "./useBriefData";
import { DataState, WidgetCard, type WidgetProps } from "./WidgetCard";
import { WIDGET_META } from "../../lib/brief-board";

const LISTS: ReadonlyArray<[code: string, name: string]> = [
  ["SP1500", "S&P 1500"],
  ["NYSE", "NYSE"],
  ["NASDAQ", "NASDAQ"],
];

const ROWS: ReadonlyArray<[key: keyof MarketList["rows"], label: string]> = [
  ["sectorAD", "Sector A/D"],
  ["industryAD", "Industry A/D"],
  ["advance", "% Advance"],
  ["aboveMid", "% Above Mid BB"],
];

export function MarketPerfWidget({ widget, onRemove }: WidgetProps) {
  const state = useBriefData<MarketPayload>("market", fetchBriefMarket);
  return (
    <WidgetCard
      title={WIDGET_META[widget.type].title}
      live={state.data?.live}
      onRemove={onRemove}
      controls={
        <span style={LEGEND}>
          <span><span style={{ ...SWATCH, background: "var(--qf-positive)" }} />1D</span>
          <span><span style={{ ...SWATCH, background: PREV }} />2D</span>
        </span>
      }
    >
      <DataState state={state}>
        {(data) => (
          <div style={GRID}>
            {LISTS.map(([code, name]) => (
              <ListRows key={code} name={name} list={data.lists[code] ?? null} />
            ))}
          </div>
        )}
      </DataState>
    </WidgetCard>
  );
}

function ListRows({ name, list }: { name: string; list: MarketList | null }) {
  return (
    <>
      <div style={LIST_HEAD}>
        {name}
        <span style={{ ...MUTED, fontWeight: 400 }}>
          {list ? `${list.stocks.toLocaleString()} symbols` : "no data"}
        </span>
      </div>
      {list && ROWS.map(([key, label]) => <Row key={key} label={label} pair={list.rows[key]} />)}
    </>
  );
}

function Row({ label, pair }: { label: string; pair: DayPair }) {
  const [a, b] = pair;
  const show = (v: number | null) => (v === null ? "—" : `${Math.round(v)}%`);
  return (
    <>
      <div style={MUTED}>{label}</div>
      <DualGauge d1={a} d2={b} />
      <span style={{ ...NUM, color: a === null ? "var(--qf-text-muted)" : a >= 50 ? "var(--qf-positive)" : "var(--qf-negative)" }}>
        {show(a)}
      </span>
      <span style={NUM}><PointDelta a={a} b={b} /></span>
      <span style={NUM}>{show(b)}</span>
    </>
  );
}

const GRID: CSSProperties = {
  display: "grid", gridTemplateColumns: "minmax(96px, auto) minmax(60px, 1fr) 40px 44px 40px",
  gap: "5px 10px", alignItems: "center", fontVariantNumeric: "tabular-nums",
};
const LIST_HEAD: CSSProperties = {
  gridColumn: "1 / -1", fontWeight: 600, display: "flex", justifyContent: "space-between",
  borderBottom: "1px solid var(--qf-border)", padding: "6px 0 3px",
};
const NUM: CSSProperties = { textAlign: "right", whiteSpace: "nowrap" };
const LEGEND: CSSProperties = {
  display: "inline-flex", gap: 10, alignItems: "center", color: "var(--qf-text-muted)", fontSize: 13,
};
const SWATCH: CSSProperties = {
  display: "inline-block", width: 14, height: 4, borderRadius: 2, verticalAlign: "middle", marginRight: 4,
};
